'use client'

import { motion } from 'framer-motion'
import { 
  Footer,
  Navigation
} from '@/ui/design-system'
import { PageLayout } from '@/ui/layout'
import { Logo } from '@/ui/components'
import { 
  BookOpen, 
  MapPin, 
  Users, 
  UtensilsCrossed, 
  Camera, 
  Route,
  FileText,
  HelpCircle,
  Sparkles,
  Calendar,
  Settings,
  CheckCircle2 } from '@/ui/icons'
import { Link } from 'react-router-dom'
import { PageMeta } from '@/components/PageMeta'

export default function Documentation() {
  // Navigation configuration
  const navigationItems = [
    {
      title: "Home",
      href: "/",
    },
  ]

  const navigationActions = {
    primary: { label: "Get started", href: "/signup" },
    secondary: { label: "Sign in", href: "/login" }
  }

  // Footer configuration
  const footerSections = [
    {
      title: "Product",
      links: [
        { label: "About Us", href: "/about" },
        { label: "Sign Up", href: "/signup" },
      ]
    },
    {
      title: "Support",
      links: [
        { label: "Documentation", href: "/docs" },
        { label: "Contact Us", href: "/contact" },
        { label: "Report Issue", href: "/contact" },
      ]
    },
    {
      title: "Legal",
      links: [
        { label: "Terms of Service", href: "/terms" },
        { label: "Privacy Policy", href: "/privacy" },
      ]
    }
  ]

  const quickLinks = [
    { icon: <BookOpen className="w-5 h-5" />, title: "Getting Started", href: "#getting-started" },
    { icon: <MapPin className="w-5 h-5" />, title: "Trip Planning", href: "#trip-planning" },
    { icon: <Route className="w-5 h-5" />, title: "Routes & Waypoints", href: "#routes-waypoints" },
    { icon: <Users className="w-5 h-5" />, title: "Crew Management", href: "#crew-management" },
    { icon: <UtensilsCrossed className="w-5 h-5" />, title: "Meal Planning", href: "#meal-planning" },
    { icon: <Camera className="w-5 h-5" />, title: "Photos & Media", href: "#photos-media" },
    { icon: <Sparkles className="w-5 h-5" />, title: "Ranger AI", href: "#ranger-ai" },
    { icon: <HelpCircle className="w-5 h-5" />, title: "FAQs", href: "#faqs" },
  ]

  return (
    <PageLayout showHeader={false}>
      <PageMeta title="Documentation" description="SitRepp.io documentation for routes, waypoints, gear, and trip planning" />
      <Navigation
        logo={<Logo to="/" size="md" />}
        items={navigationItems}
        actions={navigationActions}
      />

      <div className="min-h-screen bg-earth-50">
        {/* Hero Section */}
        <section className="py-20 lg:py-32 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <motion.div
              className="text-center max-w-4xl mx-auto mb-16"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-foreground mb-6 leading-tight">
                Documentation
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed">
                Everything you need to know to plan better trips with your crew
              </p>
            </motion.div>

            {/* Quick Links Grid */}
            <motion.div
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {quickLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="flex flex-col items-center gap-3 p-6 bg-earth-50 rounded-lg hover:bg-earth-100 transition-colors group"
                >
                  <div className="text-primary group-hover:scale-110 transition-transform">
                    {link.icon}
                  </div>
                  <span className="text-sm font-medium text-foreground text-center">
                    {link.title}
                  </span>
                </a>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Content */}
        <section className="py-20 lg:py-32 px-4 sm:px-6 lg:px-8 bg-earth-50">
          <div className="max-w-6xl mx-auto">
            <div className="space-y-16">
              {/* Getting Started */}
              <motion.section
                id="getting-started"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">Getting Started</h2>
                <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3">1. Create Your Account</h3>
                    <p>
                      Sign up for a free SitRepp.io account to start planning your first trip. You can create trips, invite crew members, and access basic features with the free tier.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3">2. Complete Your Profile</h3>
                    <p>
                      Add your experience level, trip preferences, and vehicle information to help your crew understand your setup and preferences.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3">3. Create Your First Trip</h3>
                    <p>
                      Click "Create Trip" to start planning. Add a name, description, dates, and location. You can build out routes, waypoints, meals, and gear as you plan.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3">4. Invite Your Crew</h3>
                    <p>
                      Share your trip with crew members by sending invitations via email. They can join as full members or guests, depending on your trip settings.
                    </p>
                  </div>
                </div>
              </motion.section>

              {/* Trip Planning */}
              <motion.section
                id="trip-planning"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">Trip Planning</h2>
                <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    Trips are the central hub for all your overlanding adventures. Each trip contains routes, waypoints, crew members, gear lists, meal plans, and photos.
                  </p>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3">Creating a Trip</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Navigate to "My Trips" and click "Create Trip"</li>
                      <li>Enter trip name, description, start and end dates</li>
                      <li>Set a meetup location and time for your crew</li>
                      <li>Choose if the trip should be public or private</li>
                      <li>Save and start adding routes, waypoints, and crew members</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3">Public vs Private Trips</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong className="text-foreground">Private:</strong> Only invited members can view and participate</li>
                      <li><strong className="text-foreground">Public:</strong> Anyone with the link can view and join as a guest</li>
                    </ul>
                  </div>
                </div>
              </motion.section>

              {/* Routes & Waypoints */}
              <motion.section
                id="routes-waypoints"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">Routes & Waypoints</h2>
                <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    Draw routes directly on the map using Mapbox GL JS, add waypoints to organize your trip, and export GPX files for your navigation devices.
                  </p>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3">Drawing Routes</h3>
                    <p>
                      Routes are drawn client-side directly on the map. Use the drawing tools in the Itinerary Builder to create your route by clicking on the map. Routes are automatically saved and can be styled with different colors, line types, and widths.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3">Adding Waypoints</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Use the Itinerary Builder map to search for locations</li>
                      <li>Click on the map to add waypoints manually</li>
                      <li>Import GPX files to load existing waypoints</li>
                      <li>Search for POIs (gas stations, campgrounds, viewpoints)</li>
                      <li>Organize waypoints into folders for better trip management</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3">GPX Export</h3>
                    <p>
                      Export your routes as GPX files (Pro) to use with navigation apps like Gaia GPS, OnX Offroad, or Garmin devices. GPX files include all waypoints, routes, and metadata.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3">Snap to Road</h3>
                    <p>
                      Enable "Snap to Road" (Pro) to automatically align waypoints to drivable roads, ensuring your route follows actual road networks.
                    </p>
                  </div>
                </div>
              </motion.section>

              {/* Crew Management */}
              <motion.section
                id="crew-management"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">Crew Management</h2>
                <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    Invite crew members, manage roles, and coordinate with your team throughout the trip planning process.
                  </p>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3">Inviting Members</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Click "Invite Members" on your trip page</li>
                      <li>Enter email addresses and assign roles (Owner or Member)</li>
                      <li>Add a personal message to your invitation</li>
                      <li>Members receive an email with a link to join the trip</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3">Guest Access</h3>
                    <p>
                      Guests can view trip information without creating an account. They can see routes, waypoints, and basic trip details, but cannot edit or add content. Perfect for sharing trip plans with friends who aren't active users.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3">Roles & Permissions</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li><strong className="text-foreground">Owner:</strong> Full control - can edit, delete, and manage all trip aspects</li>
                      <li><strong className="text-foreground">Member:</strong> Can add waypoints, gear, meals, and photos</li>
                      <li><strong className="text-foreground">Guest:</strong> View-only access</li>
                    </ul>
                  </div>
                </div>
              </motion.section>

              {/* Meal Planning */}
              <motion.section
                id="meal-planning"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">Meal Planning</h2>
                <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    Plan meals for each day of your trip, assign cooking responsibilities, and track dietary preferences.
                  </p>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3">Adding Meals</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Navigate to the "Meals" tab in your trip</li>
                      <li>Select a date and meal type (breakfast, lunch, dinner, snack)</li>
                      <li>Enter meal name, description, and assign a cook</li>
                      <li>Add ingredients and cooking instructions</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3">Meal Templates</h3>
                    <p>
                      Save frequently used meals as templates to quickly add them to future trips. Templates remember ingredients, instructions, and cooking requirements.
                    </p>
                  </div>
                </div>
              </motion.section>

              {/* Photos & Media */}
              <motion.section
                id="photos-media"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">Photos & Media</h2>
                <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    Share photos and memories from your trips. Upload images, organize them by waypoint or date, and create a visual record of your adventures.
                  </p>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3">Uploading Photos</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Go to the "Photos" tab in your trip</li>
                      <li>Click "Upload Photos" and select images from your device</li>
                      <li>Photos are automatically optimized and stored securely</li>
                      <li>Associate photos with specific waypoints or dates</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3">Photo Organization</h3>
                    <p>
                      Organize photos by waypoint, date, or create custom albums. Photos are displayed in a gallery view and can be viewed full-screen.
                    </p>
                  </div>
                </div>
              </motion.section>

              {/* Ranger AI */}
              <motion.section
                id="ranger-ai"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">Ranger AI Assistant</h2>
                <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                  <p>
                    Ranger is an AI assistant specialized in overlanding and camping trip planning. Get expert advice on routes, gear, weather, and trip logistics.
                  </p>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3">Using Ranger</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>Ranger is available to Pro subscribers</li>
                      <li>Ask questions about route planning, gear recommendations, or trip logistics</li>
                      <li>Ranger can create waypoints and meals directly in your trip when provided with a trip ID</li>
                      <li>Get weather-aware advice and terrain assessments</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3">Example Questions</h3>
                    <ul className="list-disc pl-6 space-y-2">
                      <li>"What gear do I need for a 3-day desert overlanding trip?"</li>
                      <li>"Suggest a route from Moab to Canyonlands with scenic viewpoints"</li>
                      <li>"What are the best campsites along the Rubicon Trail?"</li>
                      <li>"Plan meals for a 5-day trip with 4 people"</li>
                    </ul>
                  </div>
                </div>
              </motion.section>

              {/* FAQs */}
              <motion.section
                id="faqs"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">Frequently Asked Questions</h2>
                <div className="space-y-8">
                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3">What&apos;s the difference between Free and Pro?</h3>
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      <strong className="text-foreground">Free:</strong> Core trip planning with sensible limits on crew size and photos. <strong className="text-foreground">Pro ($39/year):</strong> Unlimited crew, higher photo limits, Ranger AI, campsite discovery, trip weather, and advanced sharing. Businesses with custom needs can contact us for enterprise partnerships.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3">Can I export my routes to other apps?</h3>
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      Yes! Pro subscribers can export routes as GPX files, which are compatible with most navigation apps including Gaia GPS, OnX Offroad, Garmin devices, and more.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3">How do guest invitations work?</h3>
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      Guests can view trip information without creating an account. They receive a special link that allows them to see routes, waypoints, and trip details in read-only mode. Perfect for sharing plans with friends who aren't active users.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3">Is my data secure?</h3>
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      Yes. All data is encrypted in transit and at rest. We use industry-standard security practices including Row Level Security (RLS) policies, encrypted passwords, and secure authentication tokens. See our <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link> for more details.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3">Can I use SitRepp.io offline?</h3>
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      Currently, SitRepp.io requires an internet connection. However, you can export GPX files (Pro) to use with offline-capable navigation apps. We&apos;re working on offline support for future releases.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3">How do I cancel my subscription?</h3>
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      You can manage your subscription in your account settings. Cancellations take effect at the end of your current billing period, and you&apos;ll retain access to Pro features until then.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-2xl font-semibold text-foreground mb-3">What happens to my data if I delete my account?</h3>
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      When you delete your account, all your personal data, trips, and content will be permanently deleted within 30 days. You can export your trip data before deleting your account if you want to keep a backup.
                    </p>
                  </div>
                </div>
              </motion.section>

              {/* Need More Help */}
              <motion.section
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6 }}
                className="bg-earth-50 rounded-2xl p-8 lg:p-12"
              >
                <div className="text-center max-w-3xl mx-auto">
                  <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                    Still Need Help?
                  </h2>
                  <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                    Can't find what you're looking for? Our support team is here to help.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link
                      to="/contact"
                      className="inline-flex items-center justify-center px-8 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors"
                    >
                      Contact Support
                    </Link>
                    <a
                      href="mailto:support@sitrepp.io"
                      className="inline-flex items-center justify-center px-8 py-3 bg-white border border-gray-300 text-foreground rounded-lg font-medium hover:bg-gray-50 transition-colors"
                    >
                      Email Us
                    </a>
                  </div>
                </div>
              </motion.section>
            </div>
          </div>
        </section>
      </div>

      <Footer
        sections={footerSections}
        description="A collaborative workspace for overlanding crews. Coordinate trips, manage gear, plan meals, and share memories—all in one place."
      />
    </PageLayout>
  )
}

